import { dbPut } from '../core/db.js';
import { scopedCards } from '../core/load.js';
import { state } from '../core/state.js';
import { $, $$, DAY, clozeAnswers, esc, fmtDue, fmtDur, renderRich } from '../core/text.js';
import { battleHit, battleUpdateHud, renderBattleHud, renderBattleSidebar } from '../game/battle.js';
import { G, gQuestProgress } from '../game/items.js';
import { advWorldClearOverlay } from '../game/screens.js';
import { gCardHit, gEnsureObstacle } from '../game/world.js';
import { logToday } from '../progress/streak.js';
import { STREAK_BONUS, XP_PER_CARD, checkAchievements, levelInfo, renderBuddy, renderCurrency, saveProgress } from '../progress/xp.js';
import { confetti } from '../ui/confetti.js';
import { BUDDY_MILESTONES, LEVEL_COLOR, LEVEL_NAME, SFX, achPop, levelPop, toast } from '../ui/toast.js';
import { refresh, setView } from '../ui/views.js';

/* ══════════ Lernen ══════════
   Wiederholungs-Planung nach SM-2 (Anki-Stil), angepasst auf 4 Knöpfe.
   • ease  = Leichtigkeitsfaktor (min 1.3, Start 2.5)
   • interval = Tage bis zur nächsten Fälligkeit
   • level (rot/gelb/grün) wird aus dem Intervall abgeleitet – nur fürs Anzeigen.
   Reifegrenze 21 Tage = „grün / sicher gelernt" (Anki-Standard).            */
export const MIN_EASE = 1.3, START_EASE = 2.5, MATURE_DAYS = 21, MAX_INTERVAL = 365;
export const TEN_MIN = 10 * 60000;

export function levelFor(card) {
  if (!card.reps || (card.interval || 0) < 1) return 'red';
  if (card.interval < MATURE_DAYS) return 'yellow';
  return 'green';
}
export function applyRating(card, rating) {
  card.streakHard = rating === 'hard' ? card.streakHard + 1 : 0;
  card.streakGood = rating === 'good' ? card.streakGood + 1 : 0;
  schedule(card, rating);
  card.reps++;
  if (rating === 'again') { card.wrong++; card.lapses = (card.lapses || 0) + 1; }
  card.level = levelFor(card);
  return card.level;
}
export function schedule(card, rating) {
  const now = Date.now();
  let ease = card.ease || START_EASE;
  let iv = card.interval || 0;
  // Eine Karte ist „im Lernschritt", solange sie noch kein Tages-Intervall hat.
  const learning = card.reps === 0 || iv < 1;

  if (rating === 'again') {
    ease = Math.max(MIN_EASE, ease - 0.20);
    card.interval = 0;                       // zurück in den Lernschritt
    card.due = now + TEN_MIN;                // in 10 Minuten nochmal
  } else if (learning) {
    // Neue / gerade wieder gelernte Karte aus dem Lernschritt entlassen
    if (rating === 'hard') { card.interval = 0; card.due = now + TEN_MIN; }
    else if (rating === 'good') { card.interval = 1; card.due = now + DAY; }
    else { ease += 0.05; card.interval = 4; card.due = now + 4 * DAY; }   // easy
  } else {
    // Karte ist schon „reif genug" – echtes SM-2-Wachstum
    if (rating === 'hard') { ease = Math.max(MIN_EASE, ease - 0.15); iv = Math.max(1, Math.round(iv * 1.2)); }
    else if (rating === 'good') { iv = Math.max(1, Math.round(iv * ease)); }
    else { ease += 0.15; iv = Math.max(1, Math.round(iv * ease * 1.3)); } // easy
    card.interval = Math.min(iv, MAX_INTERVAL);
    card.due = now + card.interval * DAY;
  }
  card.ease = Math.round(ease * 100) / 100;
}
export function buildQueue(pool, size, focus = 'due-first') {
  const now = Date.now();
  const w = { red: 0, yellow: 1, green: 2 };
  const shuf = (a) => a.map((c) => ({ c, r: Math.random() })).sort((x, y) => w[x.c.level] - w[y.c.level] || x.r - y.r).map((x) => x.c);
  const spread = (arr, prevDeck) => {
    const out = []; const left = [...arr];
    while (left.length) {
      const last = out.length ? out[out.length - 1].deckId : prevDeck;
      let i = left.findIndex((c) => c.deckId !== last);
      if (i === -1) i = 0;
      out.push(left.splice(i, 1)[0]);
    }
    return out;
  };
  if (focus === 'mixed') return spread(shuf(pool)).slice(0, size);
  const dueCards = pool.filter((c) => (c.due || 0) <= now);
  if (focus === 'due-only') return spread(shuf(dueCards)).slice(0, size);
  const due = spread(shuf(dueCards));
  const rest = spread(shuf(pool.filter((c) => (c.due || 0) > now)), due.length ? due[due.length - 1].deckId : null);
  return [...due, ...rest].slice(0, size);
}
export function startSession(pool, battle) {
  const cards = pool || scopedCards();
  if (!cards.length) return;
  const queue = buildQueue(cards, Math.min(state.size, cards.length), state.focus);
  if (!queue.length) { toast('Gerade nichts fällig – wähl „Alles gemischt" oder „Fällige zuerst".'); return; }
  state.session = {
    queue, i: 0, mode: state.mode, studyMode: state.studyMode,
    counts: { again: 0, hard: 0, good: 0, easy: 0 },
    ms: 0, shownAt: 0, target: queue.length, answered: false,
    battle: !!battle && G().on, combo: 0, startedAt: Date.now(), reward: 0,
  };
  setView('study');
  battleSetup(state.session.battle);
  $('#summary').classList.add('hidden');
  $('#stage').classList.remove('hidden');
  showCard();
}
/* Kampfmodus-Chrome ein-/ausblenden (Wissensduell). */
export function battleSetup(on) {
  const v = document.getElementById('view-study');
  if (v) v.classList.toggle('battle', !!on);
  ['#battle-head', '#battle-hud', '#battle-sidebar'].forEach((s) => { const el = $(s); if (el) el.classList.toggle('hidden', !on); });
  const sh = document.querySelector('#view-study .study-head'); if (sh) sh.classList.toggle('hidden', !!on);
  if (on) { gEnsureObstacle(); renderBattleHud(); renderBattleSidebar(); }
}
export function faceHTML(side, card, label, clozeMode) {
  const deck = state.decks.find((d) => d.id === card.deckId);
  const course = state.courses.find((c) => c.id === card.courseId);
  const flags = (card.fav ? '★' : '') + (card.later ? '⏰' : '');
  const txt = side.text
    ? (clozeMode !== undefined ? renderRich(side.text, { cloze: clozeMode === 'reveal' }) : renderRich(side.text))
    : '';
  const tags = (card.tags && card.tags.length)
    ? `<div class="face-tags">${card.tags.map((t) => `<span class="face-tag">#${esc(t)}</span>`).join('')}</div>` : '';
  return `<div class="face-top">
      <span class="face-deck">${esc(course?.name || '')} › ${esc(deck?.name || '')}</span>
      <span class="face-status"><span class="pill" style="background:${LEVEL_COLOR[card.level]}"></span>
        ${LEVEL_NAME[card.level]} · ${fmtDue(card)}${card.reps ? ` · ${card.reps}×` : ' · neu'}</span>
    </div>
    ${flags ? `<div class="face-flags">${flags}</div>` : ''}
    ${side.image ? `<img class="face-img" src="${side.image}" alt="">` : ''}
    ${txt ? `<div class="face-text">${txt}</div>` : ''}
    ${tags}
    <div class="face-side">${label}</div>`;
}
/* Antwort-Vergleich fürs Schreiben (ignoriert Groß/Klein, Akzente, Satzzeichen) */
export const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9äöüß ]/gi, '').replace(/\s+/g, ' ').trim();
export function lev(a, b) {
  const m = a.length, n = b.length; if (!m) return n; if (!n) return m;
  const d = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) for (let j = 1; j <= n; j++)
    d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return d[m][n];
}

export function showCard() {
  const s = state.session;
  const card = s.queue[s.i];
  const rev = s.mode === 'reverse';
  const isCloze = !!card.cloze;
  const first = rev ? card.back : card.front, second = rev ? card.front : card.back;
  s.answerText = isCloze ? clozeAnswers(card.front.text) : (second.text || '');
  // Bei Bild-Antwort automatisch auf „Klassisch" zurückfallen
  s.curMode = (s.studyMode !== 'flip' && !s.answerText) ? 'flip' : s.studyMode;
  s.answered = false;
  $('#flashcard').className = 'flashcard pc-' + card.color + (s.curMode !== 'flip' ? ' locked' : '');
  if (isCloze) {
    // Lückentext: beide Seiten aus front.text – Frage mit Lücken, Antwort mit Lösung.
    $('#face-front').innerHTML = faceHTML(card.front, card, 'Frage', 'gap');
    $('#face-back').innerHTML = faceHTML(card.front, card, 'Antwort', 'reveal');
  } else {
    $('#face-front').innerHTML = faceHTML(first, card, rev ? 'Antwort' : 'Frage');
    $('#face-back').innerHTML = faceHTML(second, card, rev ? 'Frage' : 'Antwort');
  }
  // Ohne Animation auf die Vorderseite zurücksetzen – sonst blitzt beim Kartenwechsel
  // kurz die Rückseite (= Lösung) der NÄCHSTEN Karte auf.
  const fi = $('#flip-inner');
  fi.style.transition = 'none';
  fi.classList.remove('flipped');
  void fi.offsetWidth;                 // Reflow erzwingen, damit der Sprung sofort greift
  fi.style.transition = '';
  ['#answer-row', '#answer-fb', '#write-box', '#quiz-box', '#hint-box'].forEach((x) => $(x).classList.add('hidden'));
  $('#btn-fav').classList.toggle('on', card.fav); $('#btn-fav').textContent = card.fav ? '★' : '☆';
  $('#btn-later').classList.toggle('on', card.later);
  $('#btn-hint').style.display = card.hint ? '' : 'none';
  $('#study-counter').textContent = `${s.i + 1} / ${s.queue.length}`;
  $('#study-bar').style.width = ((s.i / s.queue.length) * 100) + '%';

  $('#flip-hint').classList.toggle('hidden', s.curMode !== 'flip');
  if (s.curMode === 'write') { const inp = $('#write-input'); inp.value = ''; $('#write-box').classList.remove('hidden'); setTimeout(() => inp.focus(), 30); }
  else if (s.curMode === 'quiz') prepQuiz(card, rev);
  s.shownAt = Date.now();
  if (s.battle) { battleUpdateHud(); renderBattleSidebar(); }
}
export function flip(force) {
  const s = state.session;
  if (s && s.curMode !== 'flip') return;   // Schreiben/Quiz: das Aufdecken übernimmt revealAnswer
  const inner = $('#flip-inner');
  const flipped = inner.classList.contains('flipped');
  if (force === 'back' && !flipped) return;
  inner.classList.toggle('flipped');
  const now = inner.classList.contains('flipped');
  $('#flip-hint').classList.toggle('hidden', now);
  $('#answer-row').classList.toggle('hidden', !now);
  SFX.flip();
}
export function revealAnswer(correct) {
  const s = state.session;
  s.answered = true;
  $('#write-box').classList.add('hidden');
  $('#flip-inner').classList.add('flipped');
  $('#flip-hint').classList.add('hidden');
  const fb = $('#answer-fb');
  fb.className = 'answer-fb ' + (correct ? 'ok' : 'no');
  fb.innerHTML = correct ? '✓ Richtig!' : `✗ Nicht ganz · richtig: <b>${esc(s.answerText)}</b>`;
  fb.classList.remove('hidden');
  $('#answer-row').classList.remove('hidden');
  SFX[correct ? 'good' : 'again']();
}
export function checkWrite() {
  const s = state.session;
  if (!s || s.answered || s.curMode !== 'write') return;
  const a = norm($('#write-input').value), b = norm(s.answerText);
  const correct = a.length > 0 && (a === b || (b.length > 4 && lev(a, b) <= 1));
  revealAnswer(correct);
}
export function prepQuiz(card, rev) {
  const s = state.session;
  const correct = s.answerText;
  const others = [...new Set(state.cards
    .filter((c) => c.id !== card.id)
    .map((c) => (rev ? c.front : c.back).text)
    .filter((t) => t && norm(t) !== norm(correct)))];
  for (let i = others.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [others[i], others[j]] = [others[j], others[i]]; }
  const opts = [correct, ...others.slice(0, 3)];
  if (opts.length < 2) {   // zu wenig Karten → klassisch
    s.curMode = 'flip'; $('#flashcard').classList.remove('locked'); $('#flip-hint').classList.remove('hidden'); return;
  }
  for (let i = opts.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [opts[i], opts[j]] = [opts[j], opts[i]]; }
  s.quizOpts = opts;
  $('#quiz-box').innerHTML = opts.map((t, i) => `<button class="quiz-opt" data-qopt="${i}"><span class="qkey">${i + 1}</span><span>${esc(t)}</span></button>`).join('');
  $('#quiz-box').classList.remove('hidden');
  $$('[data-qopt]').forEach((b) => { b.onclick = () => quizPick(+b.dataset.qopt); });
}
export function quizPick(i) {
  const s = state.session;
  if (!s || s.answered || s.curMode !== 'quiz' || !s.quizOpts) return;
  const correct = norm(s.quizOpts[i]) === norm(s.answerText);
  $$('#quiz-box .quiz-opt').forEach((b, idx) => {
    if (norm(s.quizOpts[idx]) === norm(s.answerText)) b.classList.add('correct');
    else if (idx === i) b.classList.add('wrong');
    b.disabled = true;
  });
  revealAnswer(correct);
}
export async function rate(rating) {
  const s = state.session;
  const card = s.queue[s.i];
  const took = Math.min(Date.now() - s.shownAt, 5 * 60000);
  const before = card.level;
  const wasDue = (card.due || 0) <= s.shownAt;   // fürs Wissensabenteuer: vor applyRating erfassen
  const isNew = card.reps === 0;
  const p0 = state.progress;
  // Schnappschuss für „Zurücknehmen" (nur letzte Bewertung)
  s.undo = {
    i: s.i, took, courseId: card.courseId,
    card: { ...card }, counts: { ...s.counts }, ms: s.ms,
    xp: p0.xp, rated: p0.rated, coins: p0.coins, gems: p0.gems,
    cur: { ...p0.cur }, best: { ...p0.best },
  };
  applyRating(card, rating);
  card.totalMs += took; s.ms += took; s.counts[rating]++;
  await dbPut('cards', card);
  await logToday(1, took, card.courseId);
  SFX[rating]();

  // Wissensabenteuer: Karte = Wissensimpuls gegen das aktuelle Lernhindernis
  if (G().on) {
    try {
      const hit = gCardHit(rating, took, wasDue, isNew);
      if (s.battle) {
        s.combo = (rating === 'good' || rating === 'easy') ? (s.combo || 0) + 1 : (rating === 'again' ? 0 : (s.combo || 0));
        gQuestProgress('combo', s.combo, true);
        if (hit) s.reward = (s.reward || 0) + (hit.reward || 0);
        battleHit(hit);
      }
      // Beim normalen Lernen (über die Übersicht) läuft das Abenteuer still im Hintergrund –
      // keine Schadenszahlen. Nur der Aufstieg wird kurz gemeldet.
      if (hit && hit.levelUps > 0) setTimeout(() => toast(`🎉 Lernstufe ${G().charLvl}! +${hit.levelUps} Schatztruhe${hit.levelUps > 1 ? 'n' : ''}`), 500);
      // Welt befreit (Boss besiegt) → große Erfolgs-Meldung + Übergang zur nächsten Welt
      if (hit && hit.defeated && hit.exam) setTimeout(() => advWorldClearOverlay(hit), s.battle ? 1000 : 350);
    } catch (e) { /* Spielmodus darf das Lernen nie stören */ }
  }

  // XP + Streaks (richtig = gut/sehr leicht, falsch = nicht gewusst)
  const p = state.progress;
  const lvlBefore = levelInfo(p.xp).level;
  p.rated++;
  p.xp += XP_PER_CARD;                       // 10 XP pro Karte
  if (rating === 'good' || rating === 'easy') { p.cur.correct++; p.cur.wrong = 0; }
  else if (rating === 'again') { p.cur.wrong++; p.cur.correct = 0; }
  else { p.cur.correct = 0; p.cur.wrong = 0; }
  let bonus = false;
  if (p.cur.correct > 0 && p.cur.correct % 10 === 0) { p.xp += STREAK_BONUS; bonus = true; }  // 10er-Serie
  p.best.correct = Math.max(p.best.correct, p.cur.correct);
  p.best.wrong = Math.max(p.best.wrong, p.cur.wrong);
  await saveProgress();

  const newly = await checkAchievements();
  const lvlAfter = levelInfo(p.xp).level;
  if (before !== card.level) toast(`${LEVEL_NAME[before]} → ${LEVEL_NAME[card.level]} · ${fmtDue(card)}`);
  if (bonus) toast(`🔥 ${p.cur.correct}er-Serie · +${STREAK_BONUS} XP`);
  if (lvlAfter > lvlBefore) {
    // Lern-Buddy-Level: +1 Diamant pro Stufe, Meilensteine (5/10/20/50/100/…) geben 5
    let gemGain = 0, milestone = false;
    for (let L = lvlBefore + 1; L <= lvlAfter; L++) { if (BUDDY_MILESTONES.includes(L)) { gemGain += 5; milestone = true; } else gemGain += 1; }
    p.gems = (p.gems || 0) + gemGain;
    levelPop(lvlAfter, gemGain, milestone); SFX.win(); confetti(); renderCurrency();
  }
  if (newly.length) { newly.forEach(achPop); SFX.unlock(); confetti(); }
  renderBuddy();
  s.i++;
  if (s.i >= s.queue.length) return endSession();
  showCard();
}
export async function undoRate() {
  const s = state.session;
  const u = s && s.undo;
  if (!u) { toast('Nichts zum Zurücknehmen'); return; }
  const card = s.queue[u.i];
  Object.assign(card, u.card);          // Planung (interval/ease/level/reps/due …) zurücksetzen
  await dbPut('cards', card);
  await logToday(-1, -u.took, u.courseId);
  const p = state.progress;
  p.xp = u.xp; p.rated = u.rated; p.coins = u.coins; p.gems = u.gems;
  p.cur = u.cur; p.best = u.best;
  await saveProgress();
  s.counts = u.counts; s.ms = u.ms; s.i = u.i; s.undo = null;
  renderBuddy();
  $('#summary').classList.add('hidden');
  $('#stage').classList.remove('hidden');
  showCard();
  SFX.undo();
  undoFlash();
}
/* Deutliche Rücknahme-Animation: Karte „spult zurück" + Overlay-Hinweis */
export function undoFlash() {
  const card = $('#flashcard');
  if (card) { card.classList.remove('rewind'); void card.offsetWidth; card.classList.add('rewind'); setTimeout(() => card.classList.remove('rewind'), 650); }
  let ov = document.getElementById('undo-flash');
  if (!ov) { ov = document.createElement('div'); ov.id = 'undo-flash'; document.body.appendChild(ov); }
  ov.textContent = '↶ Zurückgenommen';
  ov.classList.remove('show'); void ov.offsetWidth; ov.classList.add('show');
  setTimeout(() => ov.classList.remove('show'), 1200);
}
export async function endSession() {
  const s = state.session;
  if (s.battle) battleSetup(false);   // Kampf-Chrome ausblenden, Rundenabschluss normal zeigen
  $('#stage').classList.add('hidden');
  $('#summary').classList.remove('hidden');
  $('#study-bar').style.width = '100%';
  $('#study-counter').textContent = `${s.queue.length} / ${s.queue.length}`;
  // Katzen-Emotion je nach Runde: viel richtig → jubeln, viel daneben → traurig, sonst zufrieden
  const okCount = s.counts.good + s.counts.easy;
  const emote = okCount === s.queue.length ? 'celebrate' : s.counts.again > okCount ? 'sad' : 'happy';
  $('#summary-buddy').innerHTML = `<img class="cat-emote" src="assets/cat_${emote}.png" alt="">`;
  $('#summary-text').textContent = `${s.queue.length} Karten in ${fmtDur(s.ms)} · ⌀ ${(s.ms / s.queue.length / 1000).toFixed(1)} s pro Karte`;
  $('#summary-stats').innerHTML = `
    <div class="sum-tile"><div class="sum-num" style="color:var(--rose-dk)">${s.counts.again}</div><div class="sum-lbl">Nicht gewusst</div></div>
    <div class="sum-tile"><div class="sum-num" style="color:var(--butter-dk)">${s.counts.hard}</div><div class="sum-lbl">Schwer</div></div>
    <div class="sum-tile"><div class="sum-num" style="color:var(--mint-dk)">${s.counts.good}</div><div class="sum-lbl">Gut</div></div>
    <div class="sum-tile"><div class="sum-num" style="color:#5b93c7">${s.counts.easy}</div><div class="sum-lbl">Sehr leicht</div></div>`;

  // Währung: 5 Coins pro 10 Karten · 1 Diamant pro 50 Karten (pro abgeschlossener Session)
  const n = s.queue.length;
  const coins = Math.floor(n / 10) * 5;
  const gems = Math.floor(n / 50);
  state.progress.coins += coins; state.progress.gems += gems;
  await saveProgress();
  $('#summary-reward').innerHTML = (coins || gems)
    ? `<span class="cur-earn"><img src="assets/icon_coin.png" alt="Coins">+${coins}</span>
       ${gems ? `<span class="cur-earn"><img src="assets/icon_gem.png" alt="Diamanten">+${gems}</span>` : ''}`
    : '<span class="page-sub">Lern 10+ Karten in einer Runde für Coins ✦</span>';

  // Runden-Erfolge
  const nonzero = ['again', 'hard', 'good', 'easy'].map((k) => s.counts[k]).filter((x) => x > 0);
  const round = {
    roundLen: s.queue.length,
    roundAvg: s.ms / s.queue.length,
    roundUniform: nonzero.length === 1,
    roundFlawless: (s.counts.good + s.counts.easy) === s.queue.length,
  };
  const newly = await checkAchievements(round);
  if (newly.length) { newly.forEach(achPop); SFX.unlock(); }

  if (s.queue.length >= s.target) { confetti(); SFX.win(); }

  // Wissensabenteuer: Rundenbilanz + Zeitstempel
  const g = G();
  if (g.on) {
    g.lastStudy = Date.now();
    if (g.sessionDefeats > 0) {
      $('#summary-reward').innerHTML += ` <span class="cur-earn">⚔ ${g.sessionDefeats} Hindernis${g.sessionDefeats === 1 ? '' : 'se'} besiegt</span>`;
    }
    g.sessionDefeats = 0;
    await saveProgress();
  }
  // Rundenabschluss-Buttons: im Kampf „Nochmal kämpfen" + „Zu den Upgrades"
  const inBattle = !!s.battle;
  const again = $('#btn-again'); if (again) again.textContent = inBattle ? '⚔ Nochmal kämpfen' : 'Nochmal';
  const upg = $('#btn-upgrades'); if (upg) upg.classList.toggle('hidden', !inBattle);
  renderBuddy();
  refresh();
}

